import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { useCheckins } from '../checkins-context';
import {
  emptyState,
  listBox,
  paginationRow,
  panelCard,
  statsRow,
} from '../styles';
import { useAppAuth } from 'src/context/use-app-auth';
import { useTranslation } from 'src/text';
import { Checkin } from 'src/api/types/checkin-types';
import { sendPromotionToCheckins } from 'src/api/services/checkins-service';
import { brand } from 'src/themes/palette';
import { HeaderRow } from './header-row';
import { FilterRow } from './filter-row';
import { StatPill } from './stat-pill';
import { PromotionDialog } from './promotion-dialog';
import { CheckinRow } from './checkin-row';

const formatDate = (value: number) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(value);

const CheckinsPanel: React.FC = () => {
  const { location } = useAppAuth();
  const { settingsLabels } = useTranslation();
  const {
    loading,
    loadingPage,
    hasMore,
    range,
    setRange,
    optInOnly,
    setOptInOnly,
    search,
    setSearch,
    currentPage,
    totalPages,
    visibleCheckins,
    filteredCheckins,
    recipientsCount,
    stats,
    handleNextPage,
    handlePrevPage,
    refresh,
  } = useCheckins();

  const [promoOpen, setPromoOpen] = useState(false);
  const [promoRecipients, setPromoRecipients] = useState<Checkin[]>([]);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const rangeValue = useMemo(() => range, [range]);

  const handleSend = async (
    recipients: Checkin[],
    content: { subject?: string; message: string },
  ) => {
    const trimmedMessage = content.message.trim();
    if (!location?.key || sending || !trimmedMessage || !recipients.length) {
      if (!trimmedMessage) {
        setToast({
          open: true,
          severity: 'error',
          message: 'Please generate or write a message before sending.',
        });
      } else if (!recipients.length) {
        setToast({
          open: true,
          severity: 'error',
          message: 'No recipients selected for this promotion.',
        });
      }
      return;
    }
    setSending(true);
    try {
      const result = await sendPromotionToCheckins({
        locationId: location.key,
        recipients: recipients.map(r => ({ name: r.name, email: r.email })),
        subject: content.subject?.trim(),
        message: trimmedMessage,
      });
      setToast({
        open: true,
        severity: result.success ? 'success' : 'error',
        message:
          (result.success &&
            settingsLabels.checkinsPromoSuccess?.(result.count)) ||
          settingsLabels.checkinsPromoFailed ||
          'Unable to send promotion right now.',
      });
      if (result.success) {
        setPromoOpen(false);
      }
    } finally {
      setSending(false);
    }
  };

  const hasLocation = !!location?.key;
  const recipientsForPromo =
    promoRecipients.length > 0 ? promoRecipients : filteredCheckins;

  return (
    <Paper elevation={0} sx={panelCard}>
      <Stack spacing={2.5}>
        <HeaderRow
          title={settingsLabels.checkinsTitle || 'QR check-ins'}
          subtitle={
            settingsLabels.checkinsSubtitle ||
            'Everyone who scanned your review QR and left their details.'
          }
          onRefresh={refresh}
          onSendAll={() => {
            setPromoRecipients(filteredCheckins);
            setPromoOpen(true);
          }}
          disableActions={!hasLocation || loading}
          sendLabel={
            settingsLabels.checkinsSendCta || `Send to ${recipientsCount || 0}`
          }
        />

        <FilterRow
          search={search}
          onSearch={setSearch}
          range={rangeValue as '7d' | '30d' | 'all'}
          onRangeChange={setRange}
          optInOnly={optInOnly}
          onOptInToggle={setOptInOnly}
          rangeLabel={settingsLabels.checkinsRangeLabel || 'Time range'}
          placeholders={{
            search:
              settingsLabels.checkinsSearchPlaceholder ||
              'Search name or email',
            last7: settingsLabels.checkinsRange7d || 'Last 7 days',
            last30: settingsLabels.checkinsRange30d || 'Last 30 days',
            all: settingsLabels.checkinsRangeAll || 'All time',
            optIn: settingsLabels.checkinsOptInOnly || 'Opted-in only',
          }}
        />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          sx={statsRow}>
          <StatPill
            label={settingsLabels.checkinsStatTotal || 'Total check-ins'}
            value={stats.total}
          />
          <StatPill
            label={settingsLabels.checkinsStatOptIn || 'Opted-in'}
            value={stats.optedIn}
            color={brand.green}
          />
          <StatPill
            label={settingsLabels.checkinsStatRecent || 'This week'}
            value={stats.recent}
            color={brand.secondary}
          />
        </Stack>

        {!hasLocation && (
          <Alert severity="info">
            {settingsLabels.checkinsSelectLocation ||
              'Pick a location to see QR check-ins.'}
          </Alert>
        )}

        {(loading || loadingPage) && <LinearProgress />}

        {!loading && hasLocation && visibleCheckins.length === 0 && (
          <Box sx={emptyState}>
            <Typography variant="subtitle1" fontWeight={600}>
              {settingsLabels.checkinsEmptyTitle || 'No check-ins yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {settingsLabels.checkinsEmptySubtitle ||
                'As soon as guests scan and submit the form, they will appear here.'}
            </Typography>
          </Box>
        )}

        {!loading && visibleCheckins.length > 0 && (
          <Box sx={listBox}>
            {visibleCheckins.map((entry, idx) => (
              <CheckinRow
                key={entry.key || `${entry.email}-${entry.createdAt}-${idx}`}
                entry={entry}
                onSendPromo={selected => {
                  setPromoRecipients([selected]);
                  setPromoOpen(true);
                }}
                sending={sending}
                hasLocation={hasLocation}
                formatDate={formatDate}
                labels={{
                  unknownVisitor:
                    settingsLabels.checkinsUnknownVisitor || 'Visitor',
                  optIn: settingsLabels.checkinsOptInChip || 'Opted in',
                  noOptIn: settingsLabels.checkinsNoOptInChip || 'No opt-in',
                  sendOne: settingsLabels.checkinsSendOne || 'Send promo',
                }}
              />
            ))}
            <Divider sx={{ border: 'none' }} />
          </Box>
        )}

        {!loading && filteredCheckins.length > 0 && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
            sx={paginationRow}>
            <Typography variant="body2" color="text.secondary">
              {`Page ${currentPage + 1} of ${totalPages} • Showing ${
                visibleCheckins.length
              } of ${recipientsCount}`}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePrevPage}
                disabled={currentPage === 0 || loadingPage}>
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleNextPage}
                disabled={
                  (!hasMore && currentPage >= totalPages - 1) || loadingPage
                }>
                Next
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast(t => ({ ...t, open: false }))}>
        <Alert
          severity={toast.severity}
          onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>

      <PromotionDialog
        open={promoOpen}
        sending={sending}
        recipientsCount={recipientsForPromo.length}
        onClose={() => {
          setPromoOpen(false);
          setPromoRecipients([]);
        }}
        onSend={content => handleSend(recipientsForPromo, content)}
        labels={{
          title: settingsLabels.checkinsPromoTitle || 'Send promotion',
          body: (count: number) => {
            const body = settingsLabels.checkinsPromoBody;
            if (typeof body === 'function') return body(count);
            return (
              body ||
              `This will go to ${count} contact(s) in your filtered list.`
            );
          },
          subject: settingsLabels.checkinsPromoSubject || 'Subject',
          message: settingsLabels.checkinsPromoMessage || 'Message',
          channel: 'Channel',
          image: 'Image URL',
          upload: 'Upload image',
          cancel: settingsLabels.checkinsPromoCancel || 'Cancel',
          send: settingsLabels.checkinsPromoSend || 'Send',
          sending: settingsLabels.checkinsPromoSending || 'Sending…',
        }}
      />
    </Paper>
  );
};

export { CheckinsPanel };
